import { useState, useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle,
  User,
  Briefcase,
  Users,
  // Shield,
  ChevronRight,
} from "lucide-react";
import { Employee, Dependent } from "@shared/schema";
import { toast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
// import { useClientPlans } from "@/hooks/useClientPlans";

// Import setup screen components (to be created)
import { ProfileSetupScreen } from "./onboarding/ProfileSetupScreen";
import { EmploymentDetailsScreen } from "./onboarding/EmploymentDetailsScreen";
import { DependentsSetupScreen } from "./onboarding/DependentsSetupScreen";
// Benefits enrollment removed from onboarding — employees use open enrollment instead
// import { BenefitsEnrollmentScreen } from "./onboarding/BenefitsEnrollmentScreen";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
}

export default function NewHireOnboarding() {
  const [, navigate] = useLocation();
  const [currentStep, setCurrentStep] = useState(0);

  // Fetch employee data
  const { data: employee, isLoading: employeeLoading } = useQuery<Employee>({
    queryKey: ["/api/employees/me"],
  });

  // Fetch dependents (use /me route for self-service access)
  const { data: dependents = [] } = useQuery<Dependent[]>({
    queryKey: ["/api/employees/me/dependents"],
    queryFn: async () => {
      const res = await fetch("/api/employees/me/dependents");
      if (!res.ok) throw new Error("Failed to fetch dependents");
      return res.json();
    },
    enabled: !!employee?.id,
  });

  // Plans fetch removed — benefits enrollment handled via open enrollment
  // const { plans } = useClientPlans(employee?.clientId);

  // Debug logging
  console.log("[NewHireOnboarding] State:", {
    employee: employee ? { id: employee.id, clientId: employee.clientId, hireDate: employee.hireDate } : null,
    employeeLoading,
    dependentsCount: dependents.length,
    currentStep,
  });

  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: "profile",
      title: "Complete Profile",
      description: "Update your personal information",
      icon: <User className="h-5 w-5" />,
      completed: false,
    },
    {
      id: "employment",
      title: "Employment Details",
      description: "Review your employment information",
      icon: <Briefcase className="h-5 w-5" />,
      completed: false,
    },
    {
      id: "dependents",
      title: "Add Dependents",
      description: "Add family members for coverage",
      icon: <Users className="h-5 w-5" />,
      completed: false,
    },
    // Benefits enrollment removed from onboarding — employees use open enrollment instead
    // {
    //   id: "benefits",
    //   title: "Enroll in Benefits",
    //   description: "Select your benefit plans",
    //   icon: <Shield className="h-5 w-5" />,
    //   completed: false,
    // },
  ]);

  // Check if onboarding is already completed
  useEffect(() => {
    if (employee?.onboardingCompletedAt) {
      navigate("/");
    }
  }, [employee?.onboardingCompletedAt, navigate]);

  const progress = (currentStep / steps.length) * 100;

  const handleStepComplete = () => {
    const updatedSteps = [...steps];
    updatedSteps[currentStep].completed = true;
    setSteps(updatedSteps);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All steps completed
      completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    try {
      const res = await fetch("/api/employees/me/complete-onboarding", { method: "POST" });
      if (!res.ok) {
        const err = await res.json();
        toast({
          title: "Cannot complete onboarding",
          description: err.message || "Please complete your profile first.",
          variant: "destructive",
        });
        // Jump back to profile step so they can fill in missing fields
        setCurrentStep(0);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/employees/me"] });
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Onboarding Complete!",
      description: "Welcome to the benefits portal. You can now access all features.",
      variant: "success",
    });
    navigate("/");
  };

  const handleSkipStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  if (employeeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading your information...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-destructive">
              Unable to load employee information. Please contact your administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-8">
      <div className="container max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Welcome, {employee.firstName}!
          </h1>
          <p className="text-muted-foreground">
            Let's get you set up. Complete the following steps to access your
            benefits.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Indicators */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="text-center">
              <div
                className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-colors ${
                  index < currentStep
                    ? "bg-green-500 text-white"
                    : index === currentStep
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {step.completed ? (
                  <CheckCircle className="h-6 w-6" />
                ) : (
                  step.icon
                )}
              </div>
              <p
                className={`text-xs font-medium ${
                  index === currentStep ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {step.title}
              </p>
            </div>
          ))}
        </div>

        {/* Current Step Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {steps[currentStep].icon}
              {steps[currentStep].title}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {steps[currentStep].description}
            </p>
          </CardHeader>
          <CardContent>
            {currentStep === 0 && (
              // ADR (ONB-7): no `onSkip` on the Profile step. DOB + address are
              // required up front; skipping silently breaks age-based premium
              // calc on /benefits. Skip stays on Dependents (optional).
              <ProfileSetupScreen
                employee={employee}
                onComplete={handleStepComplete}
              />
            )}
            {currentStep === 1 && (
              <EmploymentDetailsScreen
                employee={employee}
                onComplete={handleStepComplete}
                onSkip={handleSkipStep}
              />
            )}
            {currentStep === 2 && (
              <DependentsSetupScreen
                employee={employee}
                dependents={dependents}
                onComplete={handleStepComplete}
                onSkip={handleSkipStep}
              />
            )}
            {/* Benefits enrollment removed from onboarding — employees use open enrollment instead */}
            {/* {currentStep === 3 && (
              <BenefitsEnrollmentScreen
                employee={employee}
                dependents={dependents}
                plans={plans}
                onComplete={handleStepComplete}
                onSkip={handleSkipStep}
              />
            )} */}
          </CardContent>
        </Card>

        {/* Navigation Hint */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Profile is required. Other steps can be completed later from your profile.
          </p>
        </div>
      </div>
    </div>
  );
}
